import { useEffect } from "react";
import { AlertTriangle, Lock, User as UserIcon } from "lucide-react";
import { useMeetingResponsibleOptions } from "@/hooks/useClient360Meetings";

/**
 * S4 — Meeting responsible-picker (own/team-scoped). Populated from
 * ActionScopeResolver.allowedAssigneeIds (backend; NEVER role-name). 4 states
 * (mockup v2): (1) manager → locked self · (2) supervisor → team dropdown ·
 * (3) owner∈team → default owner · (4) owner∉team (D3=A) → default self + warning.
 * Out-of-team owner is NEVER a selectable option (it isn't in the options list).
 */
export function MeetingResponsibleSelect({
  holdingId,
  value,
  onChange,
  enabled = true,
}: {
  holdingId: string;
  value: string;
  onChange: (id: string) => void;
  enabled?: boolean;
}) {
  const { data, isPending } = useMeetingResponsibleOptions(holdingId, enabled);

  useEffect(() => {
    if (data && !value) onChange(data.defaultId);
  }, [data, value, onChange]);

  if (isPending || !data) {
    return <div className="h-10 w-full animate-pulse rounded-md border bg-muted/40" aria-hidden />;
  }

  const warningBanner = data.responsibleFallback && data.responsibleWarning ? (
    <div className="mt-1.5 flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[11.5px] text-warning-foreground">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{data.responsibleWarning}</span>
    </div>
  ) : null;

  // (1) manager / single option → locked field, never a dropdown.
  if (data.restricted || data.options.length <= 1) {
    const me = data.options.find((o) => o.id === (value || data.defaultId)) ?? data.options[0];
    return (
      <>
        <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
          <span className="flex items-center gap-2 truncate">
            <UserIcon className="h-4 w-4 text-muted-foreground" aria-hidden /> {me?.name ?? "Я"}
          </span>
          <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </div>
        {warningBanner}
      </>
    );
  }

  // (2)(3)(4) supervisor — team dropdown; default = owner-in-team or self (D3=A).
  return (
    <>
      <select
        value={value || data.defaultId}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {data.options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
      {warningBanner}
    </>
  );
}
